import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { FaDownload, FaEdit, FaFileExcel, FaHistory, FaSearch, FaSyncAlt, FaTimes, FaTrash, FaUpload } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";
import "./CouponManagement.css";

const API = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");

const EXPECTED_COLUMNS = [
  "ID Petani",
  "Nama Petani",
  "Institusi",
  "Desa",
  "Kecamatan",
  "Kabupaten",
  "Cluster",
  "Nilai Kupon",
];

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

const HEADER_MAP = {
  idpetani: "farmer_id",
  farmerid: "farmer_id",
  namapetani: "farmer_name",
  farmername: "farmer_name",
  institusi: "institution",
  institution: "institution",
  desa: "village",
  village: "village",
  kecamatan: "district",
  district: "district",
  kabupaten: "regency",
  kebupaten: "regency",
  kabupatenkota: "regency",
  regency: "regency",
  cluster: "cluster",
  nilaikupon: "coupon_value",
  couponvalue: "coupon_value",
  nominalkupon: "coupon_value",
};

function formatCurrency(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function getCouponStatus(row) {
  if (!Number(row.is_active)) return { key: "inactive", label: "Nonaktif" };
  const balance = Number(row.coupon_balance) || 0;
  const value = Number(row.coupon_value) || 0;
  if (balance <= 0) return { key: "redeemed", label: "Saldo habis" };
  if (row.coupon_code && balance < value) return { key: "partial", label: "Terpakai sebagian" };
  if (row.coupon_code) return { key: "available", label: "Siap digunakan" };
  return { key: "not-generated", label: "Belum dibuat" };
}

export default function CouponManagement() {
  const { user } = useContext(AuthContext);
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [historyCoupon, setHistoryCoupon] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: "5000" });
    if (search.trim()) params.set("search", search.trim());
    if (status) params.set("status", status);
    return params.toString();
  }, [search, status]);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/coupons?${query}`, {
        headers: authHeaders(user, { Accept: "application/json" }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal memuat daftar kupon.");
      setRows(data.data || []);
      setStats(data.stats || {});
    } catch (error) {
      alert(error.message || "Gagal memuat daftar kupon.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(loadCoupons, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const downloadTemplate = () => {
    const example = [{
      "ID Petani": "PTN-0001",
      "Nama Petani": "Nama Petani",
      Institusi: "Nama Institusi",
      Desa: "Nama Desa",
      Kecamatan: "Nama Kecamatan",
      Kabupaten: "Nama Kabupaten",
      Cluster: "Cluster 1",
      "Nilai Kupon": 100000,
    }];
    const sheet = XLSX.utils.json_to_sheet(example, { header: EXPECTED_COLUMNS });
    sheet["!cols"] = EXPECTED_COLUMNS.map((name) => ({ wch: Math.max(16, name.length + 3) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Daftar Kupon");
    XLSX.writeFile(workbook, "Template_Daftar_Kupon_Petani.xlsx");
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const sourceRows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      if (!sourceRows.length) throw new Error("File Excel tidak memiliki data petani.");

      const normalizedRows = sourceRows.map((source) => {
        const target = {};
        Object.entries(source).forEach(([header, value]) => {
          const field = HEADER_MAP[normalizeHeader(header)];
          if (field) target[field] = String(value ?? "").trim();
        });
        return target;
      });

      const response = await fetch(`${API}/coupons/import`, {
        method: "POST",
        headers: authHeaders(user, { "Content-Type": "application/json", Accept: "application/json" }),
        credentials: "include",
        body: JSON.stringify({ rows: normalizedRows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal mengimpor daftar kupon.");
      alert(data.message);
      await loadCoupons();
    } catch (error) {
      alert(error.message || "File tidak dapat diproses.");
    } finally {
      setImporting(false);
    }
  };

  const deleteCoupon = async (row) => {
    const historyNote = Number(row.redemption_count) > 0
      ? " Riwayat transaksi yang sudah terjadi tetap disimpan untuk audit."
      : "";
    if (!window.confirm(`Hapus data kupon ${row.farmer_name} (${row.farmer_id})?${historyNote}`)) return;
    try {
      const response = await fetch(`${API}/coupons/${row.id}`, {
        method: "DELETE",
        headers: authHeaders(user, { Accept: "application/json" }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal menghapus data kupon.");
      await loadCoupons();
    } catch (error) {
      alert(error.message || "Gagal menghapus data kupon.");
    }
  };

  const beginEdit = (row) => {
    setEditCoupon(row);
    setEditForm({
      farmer_id: row.farmer_id || "",
      farmer_name: row.farmer_name || "",
      institution: row.institution || "",
      village: row.village || "",
      district: row.district || "",
      regency: row.regency || "",
      cluster: row.cluster || "",
      coupon_value: String(Number(row.coupon_value) || 0),
      coupon_balance: String(Number(row.coupon_balance) || 0),
      is_active: Number(row.is_active) ? "1" : "0",
    });
  };

  const changeInitialValue = (value) => {
    setEditForm((current) => {
      const previousValue = Math.max(0, Number(current.coupon_value) || 0);
      const previousBalance = Math.max(0, Number(current.coupon_balance) || 0);
      const used = Math.max(0, previousValue - previousBalance);
      const nextValue = Math.max(0, Number(value) || 0);
      return {
        ...current,
        coupon_value: value,
        coupon_balance: String(Math.max(0, nextValue - used)),
      };
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editCoupon) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/coupons/${editCoupon.id}`, {
        method: "PUT",
        headers: authHeaders(user, { "Content-Type": "application/json", Accept: "application/json" }),
        credentials: "include",
        body: JSON.stringify(editForm),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal memperbarui data kupon.");
      setEditCoupon(null);
      await loadCoupons();
    } catch (error) {
      alert(error.message || "Gagal memperbarui data kupon.");
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryCoupon(row);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API}/coupons/${row.id}/redemptions`, {
        headers: authHeaders(user, { Accept: "application/json" }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal memuat riwayat kupon.");
      setHistoryRows(data.data || []);
    } catch (error) {
      alert(error.message || "Gagal memuat riwayat kupon.");
      setHistoryCoupon(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="coupon-page">
      <header className="coupon-header">
        <div>
          <h1>Kupon Petani</h1>
          <p>Kelola daftar petani yang berhak mendapatkan kupon saat melakukan order.</p>
        </div>
        <div className="coupon-header-actions">
          <button type="button" className="coupon-btn secondary" onClick={downloadTemplate}>
            <FaDownload aria-hidden="true" /> Template Excel
          </button>
          <button type="button" className="coupon-btn primary" onClick={() => fileRef.current?.click()} disabled={importing}>
            <FaUpload aria-hidden="true" /> {importing ? "Mengimpor..." : "Upload Excel"}
          </button>
          <input ref={fileRef} className="coupon-file-input" type="file" accept=".xlsx,.xls" onChange={importFile} />
        </div>
      </header>

      <section className="coupon-summary" aria-label="Ringkasan kupon">
        <div><span>Total petani</span><strong>{Number(stats.total) || 0}</strong></div>
        <div><span>Belum dibuat</span><strong>{Number(stats.not_generated) || 0}</strong></div>
        <div><span>Siap digunakan</span><strong>{Number(stats.available) || 0}</strong></div>
        <div><span>Terpakai sebagian</span><strong>{Number(stats.partial) || 0}</strong></div>
        <div><span>Saldo habis</span><strong>{Number(stats.redeemed) || 0}</strong></div>
        <div><span>Total saldo aktif</span><strong>{formatCurrency(stats.total_value)}</strong></div>
      </section>

      <div className="coupon-toolbar">
        <label className="coupon-search">
          <FaSearch aria-hidden="true" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari ID, nama, institusi, wilayah, cluster..." />
        </label>
        <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter status kupon">
          <option value="">Semua status</option>
          <option value="not_generated">Belum dibuat</option>
          <option value="available">Siap digunakan</option>
          <option value="partial">Terpakai sebagian</option>
          <option value="redeemed">Saldo habis</option>
          <option value="inactive">Nonaktif</option>
        </select>
        <button type="button" className="coupon-btn icon" onClick={loadCoupons} disabled={loading} title="Refresh">
          <FaSyncAlt aria-hidden="true" />
        </button>
      </div>

      <div className="coupon-table-wrap">
        <table className="coupon-table">
          <thead>
            <tr>
              <th>ID Petani</th><th>Nama Petani</th><th>Institusi</th><th>Lokasi</th><th>Cluster</th>
              <th>Nilai Awal</th><th>Sisa Saldo</th><th>Kode</th><th>Status</th><th>Pemakaian</th><th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && <tr><td colSpan="11" className="coupon-empty">Belum ada daftar petani kupon.</td></tr>}
            {loading && <tr><td colSpan="11" className="coupon-empty">Memuat data...</td></tr>}
            {!loading && rows.map((row) => {
              const couponStatus = getCouponStatus(row);
              return (
                <tr key={row.id}>
                  <td><strong>{row.farmer_id}</strong></td>
                  <td>{row.farmer_name}</td>
                  <td>{row.institution || "-"}</td>
                  <td>{[row.village, row.district, row.regency].filter(Boolean).join(", ") || "-"}</td>
                  <td>{row.cluster || "-"}</td>
                  <td><strong>{formatCurrency(row.coupon_value)}</strong></td>
                  <td><strong>{formatCurrency(row.coupon_balance)}</strong></td>
                  <td><code>{row.coupon_code || "-"}</code></td>
                  <td><span className={`coupon-status ${couponStatus.key}`}>{couponStatus.label}</span></td>
                  <td>
                    {Number(row.redemption_count) > 0 ? (
                      <><strong>{Number(row.redemption_count)} transaksi</strong><br /><small>{row.last_redeemed_at ? new Date(row.last_redeemed_at).toLocaleString("id-ID") : ""}</small></>
                    ) : "-"}
                  </td>
                  <td>
                    <div className="coupon-row-actions">
                      {Number(row.redemption_count) > 0 && (
                        <button type="button" className="coupon-row-action history" onClick={() => openHistory(row)} title="Lihat riwayat penggunaan">
                          <FaHistory aria-hidden="true" />
                        </button>
                      )}
                      <button type="button" className="coupon-row-action edit" onClick={() => beginEdit(row)} title="Edit data kupon">
                        <FaEdit aria-hidden="true" />
                      </button>
                      <button type="button" className="coupon-row-action" onClick={() => deleteCoupon(row)} title="Hapus data kupon">
                        <FaTrash aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="coupon-import-note">
        <FaFileExcel aria-hidden="true" /> Upload ulang ID Petani yang sama akan memperbarui profil dan nominal, menyesuaikan sisa saldo, dan tetap menyimpan riwayat penggunaan.
      </div>

      {historyCoupon && (
        <div className="coupon-history-backdrop" onClick={() => setHistoryCoupon(null)}>
          <section className="coupon-history-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-history-title" onClick={(event) => event.stopPropagation()}>
            <header>
              <div>
                <h2 id="coupon-history-title">Riwayat Penggunaan Kupon</h2>
                <p>{historyCoupon.farmer_name} ({historyCoupon.farmer_id})</p>
              </div>
              <button type="button" onClick={() => setHistoryCoupon(null)} aria-label="Tutup riwayat"><FaTimes aria-hidden="true" /></button>
            </header>
            <div className="coupon-history-table-wrap">
              <table>
                <thead><tr><th>Tanggal</th><th>Sahabat Kakao</th><th>Jenis</th><th>Produk/Layanan</th><th>Jumlah</th><th>Order</th><th>Saldo Sebelum</th><th>Potongan</th><th>Sisa Saldo</th></tr></thead>
                <tbody>
                  {historyLoading && <tr><td colSpan="9" className="coupon-empty">Memuat riwayat...</td></tr>}
                  {!historyLoading && historyRows.length === 0 && <tr><td colSpan="9" className="coupon-empty">Kupon belum pernah digunakan.</td></tr>}
                  {!historyLoading && historyRows.map((item) => (
                    <tr key={item.id}>
                      <td>{new Date(item.redeemed_at).toLocaleString("id-ID")}</td>
                      <td><strong>{item.provider_name || "-"}</strong></td>
                      <td>{item.service_category || "-"}</td>
                      <td>{item.service_title || "-"}</td>
                      <td>{Number(item.quantity) || "-"}</td>
                      <td>#{item.order_id}</td>
                      <td>{formatCurrency(item.balance_before)}</td>
                      <td><strong>- {formatCurrency(item.discount_applied)}</strong></td>
                      <td><strong>{formatCurrency(item.balance_after)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {editCoupon && (
        <div className="coupon-history-backdrop" onClick={() => !saving && setEditCoupon(null)}>
          <section className="coupon-edit-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-edit-title" onClick={(event) => event.stopPropagation()}>
            <header>
              <div>
                <h2 id="coupon-edit-title">Edit Kupon Petani</h2>
                <p>Perbaiki data penerima dan saldo kupon.</p>
              </div>
              <button type="button" onClick={() => setEditCoupon(null)} disabled={saving} aria-label="Tutup form edit"><FaTimes aria-hidden="true" /></button>
            </header>
            <form onSubmit={saveEdit}>
              <div className="coupon-edit-grid">
                <label><span>ID Petani</span><input required maxLength="100" value={editForm.farmer_id} onChange={(event) => setEditForm({ ...editForm, farmer_id: event.target.value })} /></label>
                <label><span>Nama Petani</span><input required maxLength="180" value={editForm.farmer_name} onChange={(event) => setEditForm({ ...editForm, farmer_name: event.target.value })} /></label>
                <label><span>Institusi</span><input maxLength="180" value={editForm.institution} onChange={(event) => setEditForm({ ...editForm, institution: event.target.value })} /></label>
                <label><span>Cluster</span><input maxLength="180" value={editForm.cluster} onChange={(event) => setEditForm({ ...editForm, cluster: event.target.value })} /></label>
                <label><span>Desa</span><input maxLength="180" value={editForm.village} onChange={(event) => setEditForm({ ...editForm, village: event.target.value })} /></label>
                <label><span>Kecamatan</span><input maxLength="180" value={editForm.district} onChange={(event) => setEditForm({ ...editForm, district: event.target.value })} /></label>
                <label><span>Kabupaten</span><input maxLength="180" value={editForm.regency} onChange={(event) => setEditForm({ ...editForm, regency: event.target.value })} /></label>
                <label><span>Status</span><select value={editForm.is_active} onChange={(event) => setEditForm({ ...editForm, is_active: event.target.value })}><option value="1">Aktif</option><option value="0">Nonaktif</option></select></label>
                <label><span>Nilai Awal Kupon</span><input required type="number" min="1" step="1" value={editForm.coupon_value} onChange={(event) => changeInitialValue(event.target.value)} /></label>
                <label><span>Sisa Saldo</span><input required type="number" min="0" step="1" max={editForm.coupon_value || undefined} value={editForm.coupon_balance} onChange={(event) => setEditForm({ ...editForm, coupon_balance: event.target.value })} /></label>
              </div>
              <p className="coupon-balance-note">Mengubah nilai awal akan mempertahankan nominal yang sudah terpakai. Sisa saldo tetap dapat dikoreksi secara manual.</p>
              <footer>
                <button type="button" className="coupon-btn secondary" onClick={() => setEditCoupon(null)} disabled={saving}>Batal</button>
                <button type="submit" className="coupon-btn primary" disabled={saving}>{saving ? "Menyimpan..." : "Simpan Perubahan"}</button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
